import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/hooks/useLanguage";
import { usePremiumGate } from "@/hooks/usePremiumGate";
import { generateQuiz, type QuizQuestion } from "@/lib/study";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles, Check, X, Trophy } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/quiz")({
  component: QuizPage,
  head: () => ({
    meta: [
      { title: "Quiz - Inquira" },
      { name: "description", content: "AI-generated multiple choice quizzes to test your understanding." },
      { property: "og:title", content: "Quiz - Inquira" },
      { property: "og:description", content: "Take quick quizzes on any concept, get instant explanations." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/quiz" }],
  }),
});

interface QuizRow { id: string; title: string; concept?: string | null; questions: QuizQuestion[]; created_at: string }

function QuizPage() {
  const { user, isLoading, isPaid } = useAuth();
  const { language } = useLanguage();
  const { requirePremium } = usePremiumGate();
  const navigate = useNavigate();
  const [concept, setConcept] = useState("");
  const [generating, setGenerating] = useState(false);
  const [quizzes, setQuizzes] = useState<QuizRow[]>([]);
  const [active, setActive] = useState<QuizRow | null>(null);
  const [answers, setAnswers] = useState<number[]>([]);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { if (!isLoading && !user) navigate({ to: "/login" }); }, [isLoading, user, navigate]);

  async function load() {
    if (!user) return;
    const { data } = await supabase
      .from("quizzes")
      .select("id, title, concept, questions, created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(20);
    setQuizzes((data as unknown as QuizRow[]) ?? []);
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.id]);

  async function generate() {
    if (!user) return;
    if (!requirePremium("quiz_generation")) return;
    const c = concept.trim();
    if (c.length < 2) return toast.error("Enter a concept");
    setGenerating(true);
    try {
      const { title, questions } = await generateQuiz(c, 5, language.code);
      const { data, error } = await supabase.from("quizzes").insert({
        user_id: user.id, title: (title || c).slice(0, 200), concept: c.slice(0, 100), questions: questions as never,
      } as never).select("*").single();
      if (error) throw error;
      setConcept("");
      startQuiz(data as unknown as QuizRow);
      load();
    } catch (e) { toast.error((e as Error).message); }
    finally { setGenerating(false); }
  }

  function startQuiz(q: QuizRow) {
    setActive(q); setAnswers(new Array(q.questions.length).fill(-1)); setSubmitted(false);
  }

  async function submit() {
    if (!active || !user) return;
    let score = 0;
    for (let i = 0; i < active.questions.length; i++) if (answers[i] === active.questions[i].correctIndex) score++;
    setSubmitted(true);
    const { error } = await supabase.from("quiz_attempts").insert({
      quiz_id: active.id, user_id: user.id, score, total: active.questions.length, answers: answers as never,
    } as never);
    if (error) toast.error(error.message);
    else {
      toast.success(`Scored ${score}/${active.questions.length}`);
      const { recordProgressEvent } = await import("@/lib/progress");
      const res = await recordProgressEvent({
        type: "quiz_completed",
        subject: active.concept ?? active.title,
        correct: score,
        total: active.questions.length,
      });
      if (res?.leveled_up) toast.success(`Level up! You're now level ${res.level}.`);
    }
  }

  if (isLoading || !user) return <main className="min-h-screen bg-background p-6"><p className="text-muted-foreground">Loading…</p></main>;

  return (
    <main className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between p-4">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Home
          </Link>
          <h1 className="text-lg font-bold">Quiz</h1>
          <span className="text-xs text-muted-foreground">{quizzes.length} saved</span>
        </div>
      </header>

      <div className="mx-auto max-w-3xl space-y-6 p-4">
        {!active && (
          <>
            <section className="rounded-2xl border border-border bg-card p-4">
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold">
                <Sparkles className="h-4 w-4 text-primary" /> Generate a new quiz
              </label>
              <div className="flex gap-2">
                <input
                  value={concept} onChange={(e) => setConcept(e.target.value)}
                  placeholder="e.g. Newton's laws, photosynthesis"
                  maxLength={200}
                  className="flex-1 rounded-lg border border-border bg-background p-2 text-sm"
                />
                <Button onClick={generate} disabled={generating}>{generating ? "Generating…" : "Create quiz"}</Button>
              </div>
              {!isPaid && <p className="mt-2 text-xs text-muted-foreground">Pro feature. <Link to="/upgrade" className="underline">Upgrade</Link>.</p>}
            </section>

            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Your quizzes</h2>
              {quizzes.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No quizzes yet.</div>
              ) : (
                <div className="space-y-2">
                  {quizzes.map((q) => (
                    <button key={q.id} onClick={() => startQuiz(q)} className="flex w-full items-center justify-between rounded-lg border border-border bg-card p-3 text-left hover:bg-accent">
                      <div>
                        <p className="text-sm font-medium">{q.title}</p>
                        <p className="text-xs text-muted-foreground">{q.questions.length} questions · {new Date(q.created_at).toLocaleDateString()}</p>
                      </div>
                      <Trophy className="h-4 w-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}
            </section>
          </>
        )}

        {active && (
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">{active.title}</h2>
              <button onClick={() => setActive(null)} className="text-xs text-muted-foreground underline">Back</button>
            </div>
            {active.questions.map((q, i) => {
              const chosen = answers[i];
              const correct = q.correctIndex;
              return (
                <div key={i} className="rounded-2xl border border-border bg-card p-4">
                  <p className="mb-3 text-sm font-medium">{i + 1}. {q.question}</p>
                  <div className="space-y-2">
                    {q.choices.map((c, ci) => {
                      const isChosen = chosen === ci;
                      const isCorrect = ci === correct;
                      let cls = "border-border";
                      if (submitted) {
                        if (isCorrect) cls = "border-green-500 bg-green-500/10";
                        else if (isChosen) cls = "border-destructive bg-destructive/10";
                      } else if (isChosen) cls = "border-primary bg-primary/10";
                      return (
                        <button key={ci} disabled={submitted}
                          onClick={() => { const n = [...answers]; n[i] = ci; setAnswers(n); }}
                          className={`flex w-full items-center gap-2 rounded-lg border p-2 text-left text-sm ${cls}`}>
                          {submitted && isCorrect && <Check className="h-4 w-4 text-green-500" />}
                          {submitted && isChosen && !isCorrect && <X className="h-4 w-4 text-destructive" />}
                          <span>{c}</span>
                        </button>
                      );
                    })}
                  </div>
                  {submitted && <p className="mt-2 text-xs text-muted-foreground">{q.explanation}</p>}
                </div>
              );
            })}
            {!submitted ? (
              <Button onClick={submit} disabled={answers.some((a) => a < 0)} className="w-full">Submit</Button>
            ) : (
              <Button onClick={() => setActive(null)} variant="secondary" className="w-full">Done</Button>
            )}
          </section>
        )}
      </div>
    </main>
  );
}